import { type UserSession } from '@/stores/loginStore';

const USER_SESSION_STORAGE_KEY = 'persistedUserSession';
const useUserSession = () => {

    const getFromLocalStorage = (): UserSession => {
        const item = localStorage.getItem(USER_SESSION_STORAGE_KEY);
        if (item === null) {
            return { // default value
                isAuthenticated: false,
                twoFactorVerified: false,
                accessToken: null,
                refreshToken: null,
                userId: null,
                accessTokenExpiration: null,
                refreshTokenTimeout: undefined,
                language: import.meta.env.VITE_APP_DEFAULT_LANGUAGE,
            };
        }
        return JSON.parse(item);
    };

    const saveToLocalStorage = (value: UserSession) => {
        localStorage.setItem(USER_SESSION_STORAGE_KEY, JSON.stringify(value));
    };

    const savePartialToLocalStorage = (value: Partial<UserSession>) => {
        localStorage.setItem(
            USER_SESSION_STORAGE_KEY,
            JSON.stringify({
                ...getFromLocalStorage(),
                ...value,
            })
        );
    };

    return {
        userSession: getFromLocalStorage,
        saveUserSession: saveToLocalStorage,
        savePartialUserSession: savePartialToLocalStorage,
        getAccessToken: () => getFromLocalStorage().accessToken,
        getLanguage: () => getFromLocalStorage().language,
        getAuthorizationHeader: (): any => {
            const token = getFromLocalStorage().accessToken;
            if (token !== null) {
                return {
                    Authorization: `Bearer ${token}`,
                };
            } else {
                return {};
            }
        },
        getAccessTokenExpiration: (): string | null =>
            getFromLocalStorage().accessTokenExpiration,
        isAccessTokenExpired: (): boolean => {
            const expiration = getFromLocalStorage().accessTokenExpiration;
            if (expiration === null) {
                return true;
            }
            const date: Date = new Date(expiration);
            // expired minute before expiration
            return date.getTime() < Date.now() + 60 * 1000;
        },
        getRefreshToken: (): string | null =>
            getFromLocalStorage().refreshToken,
        getRefreshTokenTimeout: (

        ): ReturnType<typeof setTimeout> | undefined =>
            getFromLocalStorage().refreshTokenTimeout,
        getIsAuthenticated: (): boolean =>
            getFromLocalStorage().isAuthenticated,
        isTwoFactorVerified: (): boolean =>
            getFromLocalStorage().twoFactorVerified ?? true,
        getUserId: (): number | null =>
            getFromLocalStorage().userId ?? null,
    };
}

export default useUserSession;
